import type { NextPage } from 'next';
import { useEffect, useState } from 'react';
import {
  CampaignShow as PlacementsCampaignShow,
  ClicksCreativeEdit,
  ClicksCreativeCreate,
  LineItemShow,
  TargetListCreate,
  TargetListEdit,
  TargetListsList,
  FmaCreativeCreate,
  FmaCreativeEdit,
  BannerTargetingsCreate,
  BannerTargetingsEdit,
  EndemicDriversList,
  EndemicDriverCreate,
  EndemicDriverEdit,
  LeadGenCampaignsList,
  LeadGenCampaignCreate,
  LeadGenCampaignEdit,
} from 'src/client/components';
import { CampaignCreate, CampaignEdit, CampaignList, CampaignShow } from 'src/client/resources/campaigns';
import {
  LineItemCreate,
  LineItemEdit,
  LineItemList,
  LineItemShow as NativeLineItemShow,
} from 'src/client/resources/line-items';
import {
  SponsoredContentTeaserCreate,
  SponsoredContentTeaserEdit,
} from 'src/client/components/sponsored-content-teasers';
import { createPostgraphileDataProvider } from 'src/client/providers/postgraphile-data-provider';

import DescriptionIcon from '@mui/icons-material/DescriptionTwoTone';
import LineItemIcon from '@mui/icons-material/FormatListBulletedTwoTone';
import LinkIcon from '@mui/icons-material/LinkTwoTone';
import PeopleIcon from '@mui/icons-material/PeopleTwoTone';
import BlockIcon from '@mui/icons-material/Block';
import WebIcon from '@mui/icons-material/Web';
import FlipToFrontIcon from '@mui/icons-material/FlipToFrontTwoTone';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CampaignIcon from '@mui/icons-material/Campaign';
import LocalOfferIcon from '@mui/icons-material/LocalOfferTwoTone';
import InventoryIcon from '@mui/icons-material/Inventory2TwoTone';
import BusinessIcon from '@mui/icons-material/BusinessTwoTone';
import { CSAdminLayout } from 'src/client/components/layout';
import { ApolloClient, HttpLink, NormalizedCacheObject, useApolloClient } from '@apollo/client';
import { useUser, useClerk, useAuth } from '@clerk/clerk-react';

import { Admin, Resource, DataProvider, defaultTheme, defaultDarkTheme } from 'react-admin';
import { setContext } from '@apollo/client/link/context';
import { ExclusionLabelCreate, ExclusionLabelsList } from 'src/client/components/exclusion-labels';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS } from 'date-fns/locale';
import { ProductCreate, ProductEdit, ProductList, ProductShow } from 'src/client/resources/products';
import {
  SpecialtyGroupCreate,
  SpecialtyGroupEdit,
  SpecialtyGroupList,
  SpecialtyGroupShow,
} from 'src/client/resources/specialty-groups';
import { AdvertiserCreate, AdvertiserEdit, AdvertiserList, AdvertiserShow } from 'src/client/resources/advertisers';

const createDataProvider = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  getToken: () => Promise<string | null>,
) => {
  // MD-5186 Phase B: Placements REST proxy removed. Native GraphQL only.
  // Legacy route Resources (line_items / placementCampaigns) are show-only
  // redirect shells that resolve via native hooks — they do not need a data provider.
  return createPostgraphileDataProvider(apolloClient, getToken);
};

const makeLink = (getToken: () => Promise<string | null>) => {
  const authLink = setContext(async (_, { headers }) => {
    const token = await getToken();
    return token !== null
      ? {
          headers: {
            ...headers,
            Authorization: token ? `Bearer ${token}` : '',
          },
        }
      : headers;
  });

  const httpLink = new HttpLink({
    uri: process.env.NEXT_PUBLIC_GRAPHQL_API_URL,
    credentials: 'include',
  });

  return authLink.concat(httpLink);
};

const CSAdminHome: NextPage = () => {
  const [dataProvider, setDataProvider] = useState<DataProvider>();
  const apolloClient = useApolloClient();
  const { isLoaded, isSignedIn, user } = useUser();
  const { signOut } = useClerk();
  const { getToken } = useAuth();

  useEffect(() => {
    if (!dataProvider && apolloClient) {
      (async () => {
        const dataProvider = await createDataProvider(apolloClient as ApolloClient<NormalizedCacheObject>, getToken);
        setDataProvider(() => dataProvider);
      })();
    } else if (apolloClient) {
      apolloClient.link = makeLink(getToken);
    }
  }, [dataProvider, apolloClient, getToken]);

  if (!dataProvider) {
    return null;
  }

  const campaignResource = {
    list: CampaignList,
    show: CampaignShow,
    create: CampaignCreate,
    edit: CampaignEdit,
  };

  const authProvider = {
    // unused, currently
    checkError: async () => {},
    checkAuth: async () => {},
    login: async () => {},
    logout: async () => signOut(),
    getIdentity: async () => {
      if (!isLoaded || !isSignedIn || !user) {
        return Promise.reject('Not loaded or signed in');
      }
      return Promise.resolve({
        id: user.id,
        fullName: user.fullName ?? undefined,
        avatar: user.imageUrl,
      });
    },
    getPermissions: async () => 'admin',
    // ...
  };

  return (
    <div>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
        <Admin
          title="CSAdmin"
          dataProvider={dataProvider}
          authProvider={authProvider}
          layout={CSAdminLayout}
          theme={defaultTheme}
          darkTheme={defaultDarkTheme}
          defaultTheme="light"
        >
          <Resource name="campaigns" {...campaignResource} icon={DescriptionIcon} />
          <Resource
            name="lineItems"
            list={LineItemList}
            create={LineItemCreate}
            edit={LineItemEdit}
            show={NativeLineItemShow}
            options={{ label: 'Line Items' }}
            icon={LineItemIcon}
          />
          <Resource name="pioUsers" />
          {/* MD-5186 Phase B Option A: thin redirect shells only (no REST provider, no menu). */}
          <Resource name="placementCampaigns" show={PlacementsCampaignShow} />
          <Resource name="line_items" show={LineItemShow} />
          <Resource
            name="products"
            list={ProductList}
            create={ProductCreate}
            edit={ProductEdit}
            show={ProductShow}
            options={{ label: 'Products' }}
            icon={InventoryIcon}
          />
          <Resource
            name="advertisers"
            list={AdvertiserList}
            create={AdvertiserCreate}
            edit={AdvertiserEdit}
            show={AdvertiserShow}
            options={{ label: 'Advertisers' }}
            icon={BusinessIcon}
          />
          <Resource
            name="sponsoredContentTeasers"
            create={SponsoredContentTeaserCreate}
            edit={SponsoredContentTeaserEdit}
            options={{ label: 'Sponsored Content Teasers' }}
            icon={DescriptionIcon}
          />
          <Resource
            name="clicksCreatives"
            create={ClicksCreativeCreate}
            edit={ClicksCreativeEdit}
            options={{ label: 'Clicks Creatives' }}
            icon={LinkIcon}
          />
          <Resource
            name="fmaCreatives"
            create={FmaCreativeCreate}
            edit={FmaCreativeEdit}
            options={{ label: 'FMA Creatives' }}
            icon={FlipToFrontIcon}
          />
          <Resource
            name="leadGenCampaigns"
            list={LeadGenCampaignsList}
            create={LeadGenCampaignCreate}
            edit={LeadGenCampaignEdit}
            options={{ label: 'Lead Gen Campaigns' }}
            icon={CampaignIcon}
          />
          <Resource
            name="targetLists"
            list={TargetListsList}
            create={TargetListCreate}
            edit={TargetListEdit}
            options={{ label: 'Target Lists' }}
            icon={PeopleIcon}
          />
          <Resource
            name="bannerTargetings"
            create={BannerTargetingsCreate}
            edit={BannerTargetingsEdit}
            options={{ label: 'Banner Targeting' }}
            icon={WebIcon}
          />
          <Resource
            name="endemicDrivers"
            list={EndemicDriversList}
            create={EndemicDriverCreate}
            edit={EndemicDriverEdit}
            options={{ label: 'Endemic Drivers' }}
            icon={SmartToyIcon}
          />
          <Resource
            name="specialtyGroups"
            list={SpecialtyGroupList}
            show={SpecialtyGroupShow}
            create={SpecialtyGroupCreate}
            edit={SpecialtyGroupEdit}
            recordRepresentation="name"
            options={{ label: 'Specialty Groups' }}
            icon={LocalOfferIcon}
          />
          <Resource name="specialties" />
          <Resource name="professions" />
          <Resource
            name="labels"
            list={ExclusionLabelsList}
            create={ExclusionLabelCreate}
            options={{ label: 'Exclusion Labels' }}
            icon={BlockIcon}
          />
        </Admin>
        <Footer />
      </LocalizationProvider>
    </div>
  );
};

const Footer = () => (
  <div
    style={{
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 100,
      padding: 6,
      color: '#666',
      textAlign: 'center',
    }}
  >
    Copyright {new Date().getFullYear()} MDLinx. All Rights Reserved.
  </div>
);

export default CSAdminHome;
